// src/components/Breadcrumbs.jsx
// Breadcrumbs mit Schema.org BreadcrumbList für SEO

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

/**
 * Breadcrumbs-Komponente mit automatischem Schema.org Markup
 * 
 * @param {Object} props
 * @param {Array} props.items - Array von {label: string, href?: string}
 *   - Letztes Item sollte kein href haben (aktuelle Seite)
 * @param {boolean} props.showHome - Home-Icon als erstes Element zeigen (default: true)
 * @param {string} props.className - Zusätzliche CSS-Klassen
 * 
 * @example
 * <Breadcrumbs items={[
 *   { label: "Shop", href: "/shop" },
 *   { label: "Betonrohrfalle" } // Aktuelles Item ohne href
 * ]} />
 */
export default function Breadcrumbs({ items = [], showHome = true, className = "" }) {
    
    // Schema.org BreadcrumbList generieren
    useEffect(() => {
        if (!items.length) return;

        const baseUrl = "https://tuscherhelmut-betonrohrfallen.de";
        
        const listItems = [];
        
        // Home als erstes Item
        if (showHome) {
            listItems.push({
                "@type": "ListItem",
                "position": 1,
                "name": "Start",
                "item": baseUrl
            });
        }

        // Restliche Items
        items.forEach((item, index) => {
            const position = showHome ? index + 2 : index + 1;
            const listItem = {
                "@type": "ListItem",
                "position": position,
                "name": item.label
            };
            
            // Nur item URL hinzufügen wenn href vorhanden (nicht für aktuelle Seite)
            if (item.href) {
                listItem.item = `${baseUrl}${item.href}`;
            }
            
            listItems.push(listItem);
        });

        const schema = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": listItems
        };

        // Altes Script entfernen
        const existing = document.getElementById("breadcrumb-schema");
        if (existing) existing.remove();

        // Neues Script einfügen
        const script = document.createElement("script");
        script.id = "breadcrumb-schema";
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(schema);
        document.head.appendChild(script);

        return () => {
            const el = document.getElementById("breadcrumb-schema");
            if (el) el.remove();
        };
    }, [items, showHome]);

    if (!items.length) return null;

    return (
        <nav 
            aria-label="Breadcrumb" 
            className={`flex items-center gap-1.5 text-sm ${className}`}
        >
            {/* Home Link */}
            {showHome && (
                <>
                    <Link 
                        to="/" 
                        className="text-slate-400 hover:text-brand-600 transition-colors"
                        aria-label="Startseite"
                    >
                        <Home className="h-4 w-4" />
                    </Link>
                    <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                </>
            )}

            {/* Items */}
            {items.map((item, index) => {
                const isLast = index === items.length - 1;
                
                return (
                    <span key={index} className="flex items-center gap-1.5">
                        {item.href && !isLast ? (
                            <Link 
                                to={item.href}
                                className="text-slate-500 hover:text-brand-600 transition-colors font-medium"
                            >
                                {item.label}
                            </Link>
                        ) : (
                            <span className="text-slate-700 dark:text-slate-200 font-semibold">
                                {item.label}
                            </span>
                        )}
                        
                        {!isLast && (
                            <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                        )}
                    </span>
                );
            })}
        </nav>
    );
}
